// src/pages/ListasPrecios.js
//
// Gestión de listas de precios de la empresa.
//   - Tabla de listas con estado y vigencia.
//   - Botón "Nueva lista" (vacía).
//   - Botón "Duplicar lista" (con factor de ajuste opcional, ej. -25% Black Friday).
//   - Click en fila → dialog con el detalle: productos + precio en lista vs
//     precio base + botón "Editar precio individual" + "Aplicar descuento masivo".
//   - Tab "Asignaciones": qué lista está vigente en qué almacén, con vigencia.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, Typography, Stack, Tabs, Tab, Button, IconButton, Chip, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, InputAdornment,
  CircularProgress, Divider, Autocomplete, Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon         from '@mui/icons-material/Add';
import EditIcon        from '@mui/icons-material/Edit';
import PercentIcon     from '@mui/icons-material/Percent';
import LocalOfferIcon  from '@mui/icons-material/LocalOffer';
import StorefrontIcon  from '@mui/icons-material/Storefront';
import CloseIcon       from '@mui/icons-material/Close';
import RefreshIcon     from '@mui/icons-material/Refresh';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { useApp } from '../context/AppContext';
import {
  listasPrecios as apiLP, categoriasCatalogo as apiCats,
  productos as apiProductos, almacenes as apiAlm,
} from '../api/posApi';

const fmtCOP = (n) => (Number(n) || 0).toLocaleString('es-CO',
  { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
const fmtFecha = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('es-CO'); } catch { return s; }
};

const TabPanel = ({ value, index, children }) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>{value === index && children}</Box>
);

const ListasPrecios = () => {
  const { empresa, sucursales, notify } = useApp();
  const empresaId = empresa?.empresaId || empresa?.EmpresaId;

  const [tab, setTab]         = useState(0);
  const [listas, setListas]   = useState([]);
  const [cargando, setCargando] = useState(false);
  const [dialog, setDialog]   = useState(null); // {tipo:'crear'|'duplicar'|'detalle'|'asignar', ...}

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setCargando(true);
    try {
      const r = await apiLP.listar(empresaId);
      setListas(Array.isArray(r) ? r : []);
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudieron cargar las listas.', 'error');
    } finally { setCargando(false); }
  }, [empresaId, notify]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <LocalOfferIcon color="primary" fontSize="large" />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={700}>Listas de precios</Typography>
          <Typography variant="body2" color="text.secondary">
            Precios base + eventos especiales (Black Friday, temporada, mayoreo)
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<ContentCopyIcon />}
                onClick={() => setDialog({ tipo: 'duplicar' })}
                disabled={listas.length === 0}>
          Duplicar
        </Button>
        <Button variant="contained" startIcon={<AddIcon />}
                onClick={() => setDialog({ tipo: 'crear' })}>
          Nueva lista
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        <b>Precio efectivo en las ventas:</b> si un almacén tiene una lista asignada con vigencia activa,
        se usa el precio de esa lista. Si no hay lista asignada, se usa el precio base del catálogo
        (la ficha del producto). Cada lista contiene <b>todos los productos activos</b> de la empresa;
        al crearla se semilla con los precios base y luego los puedes ajustar individualmente o en bloque.
      </Alert>

      <Card sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab icon={<LocalOfferIcon fontSize="small" />} iconPosition="start" label="Listas" />
          <Tab icon={<StorefrontIcon fontSize="small" />} iconPosition="start" label="Vigencia por almacén" />
        </Tabs>
      </Card>

      <TabPanel value={tab} index={0}>
        {cargando ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : listas.length === 0 ? (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <LocalOfferIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">Aún no hay listas de precios.</Typography>
            <Button variant="contained" sx={{ mt: 2 }} startIcon={<AddIcon />}
                    onClick={() => setDialog({ tipo: 'crear' })}>
              Crear la primera
            </Button>
          </Card>
        ) : (
          <Card>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell align="center">Productos</TableCell>
                    <TableCell align="center">% ajuste</TableCell>
                    <TableCell>Vigencia</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listas.map(l => (
                    <TableRow key={l.listaPrecioId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{l.nombre}</Typography>
                        {l.descripcion && (
                          <Typography variant="caption" color="text.secondary">{l.descripcion}</Typography>
                        )}
                        {l.esPredeterminada && (
                          <Chip size="small" label="Predeterminada" sx={{ ml: 1 }} color="primary" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="center">{l.productosContados}</TableCell>
                      <TableCell align="center">
                        {l.porcentajeDescuento !== 0
                          ? <Chip size="small" label={`${l.porcentajeDescuento > 0 ? '+' : ''}${l.porcentajeDescuento}%`}
                                  color={l.porcentajeDescuento < 0 ? 'success' : 'default'} />
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {l.fechaVigenciaDesde ? fmtFecha(l.fechaVigenciaDesde) : 'sin inicio'}
                          {' — '}
                          {l.fechaVigenciaHasta ? fmtFecha(l.fechaVigenciaHasta) : 'indefinida'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {l.activa ? <Chip size="small" label="Activa" color="success" variant="outlined" />
                                  : <Chip size="small" label="Inactiva" />}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Ver / editar detalle">
                          <IconButton size="small" onClick={() => setDialog({ tipo: 'detalle', lista: l })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicar esta lista">
                          <IconButton size="small" onClick={() => setDialog({ tipo: 'duplicar', origen: l })}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <AsignacionesTab empresaId={empresaId} listas={listas} sucursales={sucursales}
                         notify={notify} onOpenAsignar={() => setDialog({ tipo: 'asignar' })} />
      </TabPanel>

      {dialog?.tipo === 'crear' && (
        <DialogCrear empresaId={empresaId} onClose={() => setDialog(null)}
                     onSaved={async () => { setDialog(null); await cargar(); notify('Lista creada.', 'success'); }}
                     onError={(m) => notify(m, 'error')} />
      )}
      {dialog?.tipo === 'duplicar' && (
        <DialogDuplicar empresaId={empresaId} listas={listas} origen={dialog.origen}
                        onClose={() => setDialog(null)}
                        onSaved={async (msg) => { setDialog(null); await cargar(); notify(msg, 'success'); }}
                        onError={(m) => notify(m, 'error')} />
      )}
      {dialog?.tipo === 'detalle' && (
        <DialogDetalle empresaId={empresaId} lista={dialog.lista}
                       onClose={() => setDialog(null)}
                       notify={notify} />
      )}
      {dialog?.tipo === 'asignar' && (
        <DialogAsignar empresaId={empresaId} listas={listas} sucursales={sucursales}
                       onClose={() => setDialog(null)}
                       onSaved={() => { setDialog(null); notify('Lista asignada al almacén.', 'success'); }}
                       onError={(m) => notify(m, 'error')} />
      )}
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Crear
// ══════════════════════════════════════════════════════════════════════

function DialogCrear({ empresaId, onClose, onSaved, onError }) {
  const [f, setF] = useState({ nombre: '', descripcion: '', desde: '', hasta: '' });
  const [g, setG] = useState(false);
  const submit = async () => {
    setG(true);
    try {
      await apiLP.crear(empresaId, {
        nombre: f.nombre.trim(), descripcion: f.descripcion.trim() || null,
        fechaVigenciaDesde: f.desde || null, fechaVigenciaHasta: f.hasta || null,
      });
      onSaved();
    } catch (e) { onError(e.mensajeUsuario || 'No se pudo crear.'); }
    finally { setG(false); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Nueva lista de precios</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          La lista se crea con <b>todos los productos activos</b> de la empresa a su precio base actual.
          Después puedes ajustar precios individualmente o en bloque desde el detalle.
        </Alert>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField autoFocus required label="Nombre" value={f.nombre}
                     onChange={(e) => setF(s => ({ ...s, nombre: e.target.value }))} />
          <TextField label="Descripción" value={f.descripcion} multiline rows={2}
                     onChange={(e) => setF(s => ({ ...s, descripcion: e.target.value }))} />
          <Stack direction="row" spacing={2}>
            <TextField type="date" label="Vigente desde" InputLabelProps={{ shrink: true }}
                       value={f.desde} onChange={(e) => setF(s => ({ ...s, desde: e.target.value }))} />
            <TextField type="date" label="Vigente hasta" InputLabelProps={{ shrink: true }}
                       value={f.hasta} onChange={(e) => setF(s => ({ ...s, hasta: e.target.value }))} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={!f.nombre.trim() || g}>
          {g ? 'Creando…' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Duplicar
// ══════════════════════════════════════════════════════════════════════

function DialogDuplicar({ empresaId, listas, origen, onClose, onSaved, onError }) {
  const [f, setF] = useState({
    origenId: origen?.listaPrecioId || listas[0]?.listaPrecioId || null,
    nombre: origen ? `${origen.nombre} (copia)` : '',
    descripcion: '',
    porcentaje: '',
    desde: '', hasta: '',
  });
  const [g, setG] = useState(false);
  const [resultado, setResultado] = useState(null);

  const submit = async () => {
    setG(true);
    try {
      const r = await apiLP.duplicar(empresaId, {
        origenListaPrecioId: Number(f.origenId),
        nuevoNombre: f.nombre.trim(),
        descripcion: f.descripcion.trim() || null,
        porcentajeAjuste: parseFloat(f.porcentaje) || 0,
        fechaVigenciaDesde: f.desde || null,
        fechaVigenciaHasta: f.hasta || null,
      });
      setResultado(r);
    } catch (e) { onError(e.mensajeUsuario || 'No se pudo duplicar.'); setG(false); }
  };

  const cerrar = () => {
    if (resultado) onSaved(
      `Lista creada. ${resultado.productosCopiados} productos copiados` +
      (resultado.productosSemilladosDesdeBase > 0
        ? ` + ${resultado.productosSemilladosDesdeBase} agregados desde precio base.`
        : '.'));
    else onClose();
  };

  return (
    <Dialog open onClose={cerrar} fullWidth maxWidth="sm">
      <DialogTitle>Duplicar lista de precios</DialogTitle>
      <DialogContent dividers>
        {!resultado ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select required label="Lista de origen"
                       value={f.origenId || ''} onChange={(e) => setF(s => ({ ...s, origenId: e.target.value }))}>
              {listas.map(l => (
                <MenuItem key={l.listaPrecioId} value={l.listaPrecioId}>{l.nombre}</MenuItem>
              ))}
            </TextField>
            <TextField autoFocus required label="Nombre de la nueva lista"
                       value={f.nombre} onChange={(e) => setF(s => ({ ...s, nombre: e.target.value }))}
                       placeholder="Ej. Black Friday 2026" />
            <TextField label="Descripción" value={f.descripcion} multiline rows={2}
                       onChange={(e) => setF(s => ({ ...s, descripcion: e.target.value }))} />
            <TextField type="number" label="Ajuste porcentual"
                       value={f.porcentaje} onChange={(e) => setF(s => ({ ...s, porcentaje: e.target.value }))}
                       InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                       helperText="Positivo sube, negativo baja. Ej: -25 aplica -25% a todos los precios." />
            <Stack direction="row" spacing={2}>
              <TextField type="date" label="Vigente desde" InputLabelProps={{ shrink: true }}
                         value={f.desde} onChange={(e) => setF(s => ({ ...s, desde: e.target.value }))} />
              <TextField type="date" label="Vigente hasta" InputLabelProps={{ shrink: true }}
                         value={f.hasta} onChange={(e) => setF(s => ({ ...s, hasta: e.target.value }))} />
            </Stack>
            <Alert severity="info" variant="outlined">
              Los productos que no estén en la lista de origen se agregan usando su
              <b> precio base del catálogo</b>, con el ajuste aplicado.
            </Alert>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="success">
              <b>{resultado.productosCopiados}</b> productos copiados desde la lista original.
            </Alert>
            {resultado.productosSemilladosDesdeBase > 0 && (
              <Alert severity="warning" variant="outlined">
                <b>{resultado.productosSemilladosDesdeBase}</b> productos <b>nuevos</b> se agregaron
                usando el precio base del catálogo. Verifica que los precios finales sean correctos.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {!resultado
          ? <>
              <Button onClick={onClose} disabled={g}>Cancelar</Button>
              <Button variant="contained" onClick={submit}
                      disabled={!f.origenId || !f.nombre.trim() || g}>
                {g ? 'Duplicando…' : 'Duplicar'}
              </Button>
            </>
          : <Button variant="contained" onClick={cerrar}>Cerrar</Button>}
      </DialogActions>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Detalle de una lista (editar precios individuales + descuento masivo)
// ══════════════════════════════════════════════════════════════════════

function DialogDetalle({ empresaId, lista, onClose, notify }) {
  const [items, setItems]     = useState([]);
  const [cargando, setCargando] = useState(false);
  const [buscar, setBuscar]   = useState('');
  const [descAbierto, setDescAbierto] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await apiLP.detalle(empresaId, lista.listaPrecioId);
      setItems(Array.isArray(r) ? r : []);
    } catch (e) { notify(e.mensajeUsuario || 'No se pudo cargar.', 'error'); }
    finally { setCargando(false); }
  }, [empresaId, lista.listaPrecioId, notify]);
  useEffect(() => { cargar(); }, [cargar]);

  const cambiar = async (item, nuevoPrecio) => {
    try {
      await apiLP.actualizarIndividual(empresaId, lista.listaPrecioId, item.productoId, nuevoPrecio);
      await cargar();
    } catch (e) { notify(e.mensajeUsuario || 'No se pudo actualizar.', 'error'); }
  };

  const filtrados = items.filter(i =>
    !buscar.trim() ||
    i.nombre.toLowerCase().includes(buscar.toLowerCase()) ||
    i.codigo.toLowerCase().includes(buscar.toLowerCase()));

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { minHeight: 600 } }}>
      <DialogTitle>
        {lista.nombre}
        <Typography variant="body2" color="text.secondary">{lista.descripcion}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField size="small" placeholder="Buscar producto" value={buscar}
                     onChange={(e) => setBuscar(e.target.value)} sx={{ flexGrow: 1 }} />
          <Button variant="outlined" startIcon={<PercentIcon />}
                  onClick={() => setDescAbierto(true)}>
            Aplicar descuento masivo
          </Button>
          <IconButton onClick={cargar}><RefreshIcon /></IconButton>
        </Stack>
        {cargando ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Producto</TableCell>
                  <TableCell align="right">Precio base</TableCell>
                  <TableCell align="right">Precio en lista</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtrados.map(i => (
                  <FilaProducto key={i.productoId} item={i} onSave={cambiar} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
      {descAbierto && (
        <DialogDescuentoMasivo empresaId={empresaId} listaId={lista.listaPrecioId}
                               onClose={() => setDescAbierto(false)}
                               onDone={async () => { setDescAbierto(false); await cargar(); notify('Descuento aplicado.', 'success'); }}
                               onError={(m) => notify(m, 'error')} />
      )}
    </Dialog>
  );
}

function FilaProducto({ item, onSave }) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor]       = useState(item.precioEnLista);
  const guardar = async () => {
    await onSave(item, parseFloat(valor) || 0);
    setEditando(false);
  };
  return (
    <TableRow hover>
      <TableCell><Typography variant="caption">{item.codigo}</Typography></TableCell>
      <TableCell><Typography variant="body2">{item.nombre}</Typography></TableCell>
      <TableCell align="right">{fmtCOP(item.precioBase)}</TableCell>
      <TableCell align="right">
        {editando ? (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
            <TextField size="small" type="number" value={valor}
                       onChange={(e) => setValor(e.target.value)}
                       sx={{ width: 120 }} autoFocus
                       onKeyDown={(e) => { if (e.key === 'Enter') guardar(); if (e.key === 'Escape') setEditando(false); }} />
            <Button size="small" onClick={guardar}>OK</Button>
            <IconButton size="small" onClick={() => setEditando(false)}><CloseIcon fontSize="small" /></IconButton>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
            <b>{fmtCOP(item.precioEnLista)}</b>
            <IconButton size="small" onClick={() => { setValor(item.precioEnLista); setEditando(true); }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </TableCell>
    </TableRow>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Descuento masivo
// ══════════════════════════════════════════════════════════════════════

function DialogDescuentoMasivo({ empresaId, listaId, onClose, onDone, onError }) {
  const [modo, setModo] = useState('todos');   // todos, categorias, productos
  const [porcentaje, setPorcentaje] = useState('');
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [selCats, setSelCats] = useState([]);
  const [selProds, setSelProds] = useState([]);
  const [g, setG] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          apiCats.listar(empresaId, true),
          apiProductos.listar(empresaId, { soloActivos: true, pageSize: 500 }),
        ]);
        setCats(Array.isArray(c) ? c.filter(x => !x.categoriaPadreId) : []);
        setProds(p?.data || p?.Data || []);
      } catch { /* silent */ }
    })();
  }, [empresaId]);

  const submit = async () => {
    if (!porcentaje) return;
    setG(true);
    try {
      await apiLP.aplicarDescuento(empresaId, listaId, {
        porcentaje: parseFloat(porcentaje),
        categoriasCatalogoIds: modo === 'categorias' ? selCats.map(c => c.categoriaCatalogoId) : null,
        marcasIds: null,
        productosIds: modo === 'productos' ? selProds.map(p => p.productoId ?? p.ProductoId) : null,
        aplicarATodos: modo === 'todos',
      });
      onDone();
    } catch (e) { onError(e.mensajeUsuario || 'No se pudo aplicar.'); }
    finally { setG(false); }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Aplicar descuento a productos de esta lista</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField required type="number" label="Porcentaje" autoFocus
                     value={porcentaje} onChange={(e) => setPorcentaje(e.target.value)}
                     InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                     helperText="Negativo baja precio (ej. -25 para 25% off), positivo sube." />
          <TextField select label="Aplicar a"
                     value={modo} onChange={(e) => setModo(e.target.value)}>
            <MenuItem value="todos">Todos los productos</MenuItem>
            <MenuItem value="categorias">Productos de ciertas categorías</MenuItem>
            <MenuItem value="productos">Productos específicos</MenuItem>
          </TextField>
          {modo === 'categorias' && (
            <Autocomplete multiple options={cats} value={selCats}
                          onChange={(_, v) => setSelCats(v)}
                          getOptionLabel={(o) => o.nombre}
                          renderInput={(p) => <TextField {...p} label="Categorías" />} />
          )}
          {modo === 'productos' && (
            <Autocomplete multiple options={prods} value={selProds}
                          onChange={(_, v) => setSelProds(v)}
                          getOptionLabel={(o) => `${o.codigo ?? o.Codigo} — ${o.nombre ?? o.Nombre}`}
                          renderInput={(p) => <TextField {...p} label="Productos" />} />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={!porcentaje || g}>
          {g ? 'Aplicando…' : 'Aplicar descuento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Vigencia por almacén
// ══════════════════════════════════════════════════════════════════════

function AsignacionesTab({ empresaId, listas, sucursales, notify, onOpenAsignar }) {
  const [asigns, setAsigns] = useState([]);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setCargando(true);
    try {
      const r = await apiLP.asignaciones(empresaId, null);
      setAsigns(Array.isArray(r) ? r : []);
    } catch (e) { notify(e.mensajeUsuario || 'No se pudo cargar.', 'error'); }
    finally { setCargando(false); }
  }, [empresaId, notify]);
  useEffect(() => { cargar(); }, [cargar]);

  const desactivar = async (a) => {
    if (!window.confirm(`¿Desactivar la asignación de "${a.listaNombre}" al almacén "${a.almacenNombre}"?`)) return;
    try {
      await apiLP.desactivar(empresaId, a.almacenListaPrecioId);
      await cargar();
      notify('Asignación desactivada.', 'success');
    } catch (e) { notify(e.mensajeUsuario || 'No se pudo desactivar.', 'error'); }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Cada almacén puede tener varias listas asignadas con distintas prioridades y vigencias.
        Al terminar un evento (Black Friday), <b>desactiva</b> la asignación de la lista promo y
        vuelve automáticamente a la lista de menor prioridad activa.
      </Alert>
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={cargar}><RefreshIcon /></IconButton>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onOpenAsignar}
                disabled={listas.length === 0}>
          Asignar lista a almacén
        </Button>
      </Stack>
      <Card>
        {cargando ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : asigns.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>Sin asignaciones registradas.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Almacén</TableCell>
                  <TableCell>Lista</TableCell>
                  <TableCell align="center">Prioridad</TableCell>
                  <TableCell>Vigencia</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {asigns.map(a => (
                  <TableRow key={a.almacenListaPrecioId}>
                    <TableCell>{a.almacenNombre}</TableCell>
                    <TableCell>{a.listaNombre}</TableCell>
                    <TableCell align="center">{a.prioridad}</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {fmtFecha(a.fechaVigenciaDesde)} — {a.fechaVigenciaHasta ? fmtFecha(a.fechaVigenciaHasta) : 'indefinida'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {a.esActiva ? <Chip size="small" label="Activa" color="success" variant="outlined" />
                                  : <Chip size="small" label="Inactiva" />}
                    </TableCell>
                    <TableCell align="right">
                      {a.esActiva && (
                        <Tooltip title="Desactivar">
                          <IconButton size="small" onClick={() => desactivar(a)}>
                            <PowerSettingsNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
}

function DialogAsignar({ empresaId, listas, sucursales, onClose, onSaved, onError }) {
  const [almacenes, setAlmacenes] = useState([]);
  const [f, setF] = useState({
    almacenId: null, listaPrecioId: null,
    fechaVigenciaDesde: new Date().toISOString().slice(0,10),
    fechaVigenciaHasta: '', prioridad: 1, notas: '',
  });
  const [g, setG] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const listasAlm = await Promise.all((sucursales || []).map(s =>
          apiAlm.listar(s.sucursalId ?? s.SucursalId)));
        setAlmacenes(listasAlm.flat().filter(Boolean));
      } catch { /* silent */ }
    })();
  }, [sucursales]);
  const submit = async () => {
    setG(true);
    try {
      await apiLP.asignar(empresaId, {
        almacenId: Number(f.almacenId),
        listaPrecioId: Number(f.listaPrecioId),
        fechaVigenciaDesde: f.fechaVigenciaDesde,
        fechaVigenciaHasta: f.fechaVigenciaHasta || null,
        prioridad: Number(f.prioridad),
        notas: f.notas || null,
      });
      onSaved();
    } catch (e) { onError(e.mensajeUsuario || 'No se pudo asignar.'); }
    finally { setG(false); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Asignar lista a almacén</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select required label="Almacén" value={f.almacenId || ''}
                     onChange={(e) => setF(s => ({ ...s, almacenId: e.target.value }))}>
            {almacenes.map(a => (
              <MenuItem key={a.almacenId} value={a.almacenId}>{a.nombre}</MenuItem>
            ))}
          </TextField>
          <TextField select required label="Lista de precios" value={f.listaPrecioId || ''}
                     onChange={(e) => setF(s => ({ ...s, listaPrecioId: e.target.value }))}>
            {listas.map(l => (
              <MenuItem key={l.listaPrecioId} value={l.listaPrecioId}>{l.nombre}</MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField required type="date" label="Vigente desde" InputLabelProps={{ shrink: true }}
                       value={f.fechaVigenciaDesde} onChange={(e) => setF(s => ({ ...s, fechaVigenciaDesde: e.target.value }))} />
            <TextField type="date" label="Vigente hasta (opcional)" InputLabelProps={{ shrink: true }}
                       value={f.fechaVigenciaHasta} onChange={(e) => setF(s => ({ ...s, fechaVigenciaHasta: e.target.value }))} />
          </Stack>
          <TextField type="number" label="Prioridad (1 = mayor)" value={f.prioridad}
                     onChange={(e) => setF(s => ({ ...s, prioridad: e.target.value }))}
                     helperText="Si un almacén tiene varias listas activas, gana la de mayor prioridad." />
          <TextField label="Notas" value={f.notas} multiline rows={2}
                     onChange={(e) => setF(s => ({ ...s, notas: e.target.value }))} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={!f.almacenId || !f.listaPrecioId || g}>
          {g ? 'Asignando…' : 'Asignar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ListasPrecios;
